import { useState } from 'react'
import { ActionIcon, Button, Group, Menu, Modal, Stack, Text, TextInput, Tooltip } from '@mantine/core'
import { addScreen, canAddScreen, isKnownScreen, screenKinds, screenMeta } from '@racedash/shared'
import type { ScreenEntry, ScreenKind } from '@racedash/shared'

/**
 * Add, rename, reorder and remove screens.
 *
 * Sits beside the tabs while editing rather than inside a dialog, because
 * every action here is about the tab strip itself and putting them somewhere
 * else would mean looking away from the thing being changed.
 */
export default function ScreenManager({
  screens,
  onChange,
  activeId,
}: {
  screens: ScreenEntry[]
  onChange: (next: ScreenEntry[]) => void
  activeId: string | null
}) {
  const [renaming, setRenaming] = useState<ScreenEntry | null>(null)
  const [name, setName] = useState('')

  const index = screens.findIndex((s) => s.id === activeId)
  const active = index >= 0 ? screens[index] : null

  const move = (by: number) => {
    if (index < 0) return
    const to = index + by
    if (to < 0 || to >= screens.length) return
    const next = [...screens]
    const [held] = next.splice(index, 1)
    if (held) next.splice(to, 0, held)
    onChange(next)
  }

  const add = (kind: ScreenKind) => {
    const next = addScreen(screens, kind)
    if (next) onChange(next)
  }

  return (
    <>
      <Group gap={4}>
        <Menu position="bottom-start" withinPortal>
          <Menu.Target>
            <Button size="xs" variant="default">
              Add screen
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            {screenKinds.map((kind) => {
              const allowed = canAddScreen(screens, kind)
              return (
                <Menu.Item key={kind} disabled={!allowed} onClick={() => add(kind)}>
                  <Text size="sm" fw={600}>
                    {screenMeta[kind].label}
                  </Text>
                  {/* Said, rather than left as a control that silently does
                      nothing: a second identical timing table is just a second
                      tab showing the same thing. */}
                  <Text size="xs" c="dimmed">
                    {allowed ? screenMeta[kind].description : 'Already on this dashboard'}
                  </Text>
                </Menu.Item>
              )
            })}
          </Menu.Dropdown>
        </Menu>

        <Tooltip label="Rename this screen" openDelay={400}>
          <ActionIcon
            variant="default"
            size="md"
            aria-label="Rename screen"
            disabled={!active || !isKnownScreen(active)}
            onClick={() => {
              if (!active) return
              setName(active.name)
              setRenaming(active)
            }}
          >
            ✎
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Move left" openDelay={400}>
          <ActionIcon
            variant="default"
            size="md"
            aria-label="Move screen left"
            disabled={index <= 0}
            onClick={() => move(-1)}
          >
            ‹
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Move right" openDelay={400}>
          <ActionIcon
            variant="default"
            size="md"
            aria-label="Move screen right"
            disabled={index < 0 || index >= screens.length - 1}
            onClick={() => move(1)}
          >
            ›
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Remove this screen" openDelay={400}>
          <ActionIcon
            variant="default"
            color="red"
            size="md"
            aria-label="Remove screen"
            disabled={!active}
            onClick={() => active && onChange(screens.filter((s) => s.id !== active.id))}
          >
            ×
          </ActionIcon>
        </Tooltip>
      </Group>

      <Modal opened={renaming !== null} onClose={() => setRenaming(null)} title="Rename screen">
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            This is the tab label, so keep it short enough to read at a glance.
          </Text>
          <TextInput
            label="Name"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            data-autofocus
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setRenaming(null)}>
              Cancel
            </Button>
            <Button
              // The established pattern: nothing to save until something has
              // actually changed.
              disabled={!name.trim() || name.trim() === renaming?.name}
              onClick={() => {
                if (!renaming) return
                onChange(
                  screens.map((s) => (s.id === renaming.id ? { ...s, name: name.trim() } : s)),
                )
                setRenaming(null)
              }}
            >
              Rename
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  )
}
